/**
 * The Graphs tab: pick a category, pick a chart, read it.
 *
 * A CATALOGUE rather than a fixed dashboard, because which chart matters
 * depends entirely on what the person came to find out. A dashboard that shows
 * everything at once shows nothing on a phone.
 *
 * Every entry declares what it NEEDS. A chart whose data is missing is listed
 * as unavailable with the reason, rather than rendering an empty frame and
 * leaving the reader to wonder whether the sync broke.
 */
import { useMemo, useState } from "react";

import {
  AgeingBar,
  ConcentrationCurve,
  Heatmap,
  MiniColumns,
  OpposingBar,
  ParetoCurve,
  RangeDots,
  RankedBars,
  RatioBar,
  Waterfall,
} from "../components/charts.js";
import { Card, Empty, SectionTitle } from "../components/ui.js";
import { ShareBar } from "../components/ShareBar.js";
import { allOf, needsMoneyLedgers, needsSales, needsStock } from "../derive/availability.js";
import type { ChartData } from "../derive/availability.js";
import {
  customerConcentration,
  deriveRfm,
  newCustomersByMonth,
  newVsReturning,
  paretoItems,
  priceSpread,
  profitWaterfall,
  salesByState,
  salesByWeekday,
  stockAgeing,
  stockTurnover,
} from "../derive/charts.js";
import { deriveDeadStock, deriveMonthOnMonth, deriveTopCustomers, deriveTopItems } from "../derive/insights.js";
import {
  deriveBalanceSheet,
  deriveProfitLoss,
  deriveSalesRegister,
  formatDate,
  formatMoney,
  formatQty,
} from "../derive/reports.js";
import { navigate } from "../routes.js";
import { useApp } from "../state.js";

type Ctx = ChartData;

type Chart = {
  id: string;
  title: string;
  question: string;
  /** null when it can be drawn; a sentence explaining why not otherwise */
  unavailable?: (ctx: Ctx) => string | null;
  render: (ctx: Ctx) => JSX.Element;
};

type Category = { id: string; title: string; blurb: string; charts: Chart[] };


/* ────────────────────────────────────────────────────────────────── sales ── */

const SALES: Chart[] = [
  {
    id: "sales-months",
    title: "Sales by month",
    question: "Are we growing?",
    unavailable: needsSales,
    render: ({ snapshot, groups, asOf }) => {
      const months = deriveSalesRegister(snapshot, groups).months.slice(0, 12).reverse();
      const mom = deriveMonthOnMonth(snapshot, asOf, groups);
      return (
        <>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <span className="tabular text-[22px] font-semibold text-ink">
              {formatMoney(mom.thisMonth)}
            </span>
            <span className="text-xs text-ink-3">
              {mom.lastLabel} {formatMoney(mom.lastMonth)}
            </span>
          </div>
          {mom.partialMonth && (
            <p className="mb-3 text-xs text-warn">
              {mom.thisLabel} is only {mom.dayOfMonth} of {mom.daysInMonth} days so far — the last
              column is not a full month.
            </p>
          )}
          <MiniColumns data={months.map((m) => ({ label: m.label, value: m.amount }))} />
        </>
      );
    },
  },
  {
    id: "sales-weekday",
    title: "Sales by weekday",
    question: "Which days carry the week?",
    unavailable: needsSales,
    render: ({ snapshot, groups }) => (
      <MiniColumns data={salesByWeekday(snapshot, groups)} />
    ),
  },
  {
    id: "sales-state",
    title: "Sales by state",
    question: "Which regions pay?",
    unavailable: allOf(needsSales, ({ snapshot, groups }) => {
      const bars = salesByState(snapshot, groups).bars;
      if (bars.length === 0) return "No customer ledger in these books has a state set in TallyPrime.";
      // One bar is not a comparison. Drawing it would imply a regional split
      // that does not exist, which is worse than saying so plainly.
      if (bars.length === 1) return `Every customer is in ${bars[0]!.label} — there is nothing to compare.`;
      return null;
    }),
    render: ({ snapshot, groups }) => {
      const report = salesByState(snapshot, groups);
      return (
        <>
          <RankedBars data={report.bars} limit={10} />
          <p className="mt-3 text-xs text-ink-3">
            Covers {report.coverage.toFixed(0)}% of sales — the rest is on customers with no state
            recorded.
          </p>
        </>
      );
    },
  },
  {
    id: "sales-new-returning",
    title: "New vs returning",
    question: "Is growth from new customers or existing ones?",
    unavailable: needsSales,
    render: ({ snapshot, groups }) => {
      const rows = newVsReturning(snapshot, groups);
      const latest = rows[rows.length - 1];
      return (
        <>
          {latest && (
            <div className="mb-4">
              <OpposingBar
                left={latest.fresh}
                right={latest.returning}
                leftLabel="New"
                rightLabel="Returning"
              />
              <p className="mt-2 text-xs text-ink-3">Latest month: {latest.month}</p>
            </div>
          )}
          <SectionTitle>Returning revenue, by month</SectionTitle>
          <MiniColumns
            data={rows.map((r) => ({ label: r.month.slice(2), value: r.returning }))}
          />
        </>
      );
    },
  },
];

/* ─────────────────────────────────────────────────────────────── products ── */

const PRODUCTS: Chart[] = [
  {
    id: "top-items-chart",
    title: "Top items",
    question: "What actually sells?",
    unavailable: allOf(needsStock, needsSales),
    render: ({ snapshot }) => (
      <RankedBars
        data={deriveTopItems(snapshot, 10).rows.map((r) => ({ label: r.item, value: r.value }))}
        limit={10}
      />
    ),
  },
  {
    id: "pareto",
    title: "How few items carry us",
    question: "Where does 80% of the money come from?",
    unavailable: allOf(needsStock, ({ snapshot }) =>
      paretoItems(snapshot).points.length < 5 ? "Too few items sold to draw a curve." : null,
    ),
    render: ({ snapshot }) => {
      const pareto = paretoItems(snapshot);
      return (
        <>
          <p className="mb-3 text-sm text-ink-2">
            <strong className="text-ink">{pareto.countTo80}</strong> of {pareto.points.length} items
            — {pareto.shareTo80.toFixed(0)}% of the range — make 80% of {formatMoney(pareto.total)}.
          </p>
          <ParetoCurve points={pareto.points} />
        </>
      );
    },
  },
  {
    id: "price-spread",
    title: "Same item, different prices",
    question: "Where is discount leaking?",
    unavailable: allOf(needsStock, ({ snapshot }) =>
      priceSpread(snapshot).length === 0
        ? "Every item sold at a single price, or too few sales each to compare."
        : null,
    ),
    render: ({ snapshot, groups }) => <PriceSpreadChart snapshot={snapshot} groups={groups} />,
  },
];

/* ────────────────────────────────────────────────────────────────── stock ── */

const STOCK: Chart[] = [
  {
    id: "stock-ageing",
    title: "Stock ageing",
    question: "How stale is what we hold?",
    // Declared rather than handled inside render, so the catalogue greys the
    // row out instead of opening a chart onto an apology.
    unavailable: allOf(needsStock, ({ snapshot, asOf }) =>
      stockAgeing(snapshot, asOf).valuesWithheld ? "Stock values are not part of this view." : null,
    ),
    render: ({ snapshot, asOf }) => <StockAgeingChart snapshot={snapshot} asOf={asOf} />,
  },
  {
    id: "dead-stock-chart",
    title: "Not selling",
    question: "What has never moved?",
    unavailable: needsStock,
    render: ({ snapshot }) => {
      const dead = deriveDeadStock(snapshot);
      return (
        <>
          <p className="mb-3 text-sm text-ink-2">
            <strong className="text-ink">{dead.items.length}</strong> items held, none sold
            {!dead.valuesWithheld && ` — ${formatMoney(dead.valueLockedUp)} tied up`}.
          </p>
          {/*
            Which items never move is worth knowing even without their value, so
            this stays available on a copy that had stock VALUES zeroed. But the
            bars are drawn from those values, and a chart of nothing needs to say
            why — RankedBars drops zero-magnitude rows and would otherwise leave
            a bare "Nothing to show." under a sentence counting real items.
          */}
          {dead.valuesWithheld ? (
            <>
              <p className="mb-3 text-xs text-ink-3">
                Stock values are not part of this view, so these cannot be ranked by what they tie
                up — they are listed as they come.
              </p>
              <ul className="space-y-1.5">
                {dead.items.slice(0, 10).map((i) => (
                  <li key={i.item} className="truncate text-[13px] text-ink-2">
                    {i.item}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <RankedBars
              data={dead.items.map((i) => ({ label: i.item, value: i.closingValue }))}
              limit={10}
              tone="var(--accent-2)"
            />
          )}
        </>
      );
    },
  },
  {
    id: "turnover",
    title: "Stock turnover",
    question: "How many times does stock cycle?",
    unavailable: allOf(needsStock, ({ snapshot }) =>
      stockTurnover(snapshot).turns === null ? "No stock values to measure against." : null,
    ),
    render: ({ snapshot }) => {
      const t = stockTurnover(snapshot);
      return (
        <>
          <RatioBar
            value={t.annualised}
            display={`${t.annualised?.toFixed(2) ?? "—"}× a year`}
            benchmark={4}
            benchmarkLabel="4×"
            higherIsBetter
            max={12}
          />
          <dl className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-3">Cost of goods sold</dt>
              <dd className="tabular text-ink">{formatMoney(t.cogs)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-3">Average stock held</dt>
              <dd className="tabular text-ink">{formatMoney(t.averageStock)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-3">Over</dt>
              <dd className="tabular text-ink">{t.windowDays} days</dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-ink-3">
            Cost of goods sold is estimated as opening stock + purchases − closing stock, because
            TallyPrime has no single cost-of-sales ledger we can rely on across every chart of
            accounts. The 4× line is a common wholesale rule of thumb, not a law.
          </p>
        </>
      );
    },
  },
];

/* ────────────────────────────────────────────────────────────── customers ── */

const CUSTOMERS: Chart[] = [
  {
    id: "top-customers-chart",
    title: "Top customers",
    question: "Who pays the bills?",
    unavailable: needsSales,
    render: ({ snapshot, groups }) => (
      <RankedBars
        data={deriveTopCustomers(snapshot, groups, 10).rows.map((r) => ({
          label: r.ledger,
          value: r.value,
        }))}
        limit={10}
      />
    ),
  },
  {
    id: "rfm",
    title: "Customer segments (RFM)",
    question: "Who are the champions, and who is slipping away?",
    unavailable: allOf(needsSales, ({ snapshot, groups, asOf }) =>
      deriveRfm(snapshot, asOf, groups).tooFew
        ? "Fewer than ten customers — quintile scoring would say nothing useful."
        : null,
    ),
    render: ({ snapshot, groups, asOf }) => (
      <RfmChart snapshot={snapshot} groups={groups} asOf={asOf} />
    ),
  },
  {
    id: "new-customers",
    title: "New customers per month",
    question: "Is acquisition slowing?",
    unavailable: needsSales,
    render: ({ snapshot, groups }) => (
      <MiniColumns data={newCustomersByMonth(snapshot, groups)} />
    ),
  },
  {
    id: "concentration",
    title: "How concentrated is the risk",
    question: "How much depends on a handful of names?",
    unavailable: needsSales,
    render: ({ snapshot, groups }) => {
      const c = customerConcentration(snapshot, groups);
      return (
        <ConcentrationCurve
          points={c.points}
          caption={`The top 5 of ${c.customers} customers are ${c.topFiveShare.toFixed(0)}% of sales. The further the curve bows from the dashed line, the more concentrated the risk.`}
        />
      );
    },
  },
];

/* ────────────────────────────────────────────────────────────── financial ── */

const FINANCIAL: Chart[] = [
  {
    id: "profit-waterfall",
    title: "Income to profit",
    question: "How does revenue become profit?",
    unavailable: needsMoneyLedgers,
    render: ({ snapshot, groups }) => {
      const pl = deriveProfitLoss(snapshot, groups);
      return <Waterfall steps={profitWaterfall(pl.totalIncome, pl.totalExpenses, pl.netProfit)} />;
    },
  },
  {
    id: "sheet-shape",
    title: "Assets and liabilities",
    question: "What is the business made of?",
    unavailable: needsMoneyLedgers,
    render: ({ snapshot, groups }) => {
      const sheet = deriveBalanceSheet(snapshot, groups);
      return (
        <>
          <OpposingBar
            left={sheet.totalAssets}
            right={sheet.totalLiabilities}
            leftLabel="Assets"
            rightLabel="Liabilities"
          />
          <div className="mt-4">
            <SectionTitle>Biggest assets</SectionTitle>
            <RankedBars data={sheet.assets.map((a) => ({ label: a.name, value: a.amount }))} />
          </div>
        </>
      );
    },
  },
];

/**
 * Stock ageing, with the buckets opened.
 *
 * A bucket that only reports a total answers half a question: "£17 lakh sat
 * still for two months" is not actionable until you can see WHICH goods. So
 * every bucket opens into its items, biggest value first.
 *
 * A component rather than an inline render because it holds selection state —
 * hooks called inside the catalogue's `render` closure would run as part of the
 * parent's render and break the moment a chart is added above it.
 */
function StockAgeingChart({ snapshot, asOf }: Pick<Ctx, "snapshot" | "asOf">) {
  const [open, setOpen] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  const ageing = useMemo(() => stockAgeing(snapshot, asOf), [snapshot, asOf]);
  const bucket = (open === null ? null : ageing.buckets[open]) ?? null;

  const LIMIT = 40;
  const rows = bucket ? (showAll ? bucket.rows : bucket.rows.slice(0, LIMIT)) : [];

  return (
    <>
      <AgeingBar
        data={ageing.buckets.map((b) => ({ label: b.label, value: b.value }))}
        total={ageing.total}
        selected={open}
        onSelect={(i) => {
          setOpen(i);
          // A fresh bucket starts collapsed again; carrying "show all" across
          // would drop 300 rows on someone who tapped a bucket of four.
          setShowAll(false);
        }}
      />

      {bucket === null ? (
        <p className="mt-3 text-xs text-ink-3">
          Measured from the last SALE of each item — a stock journal moving goods between godowns
          does not count. <strong className="text-ink-2">Tap a bucket to see its items.</strong>
        </p>
      ) : (
        <div className="mt-4">
          <div className="mb-2 flex items-baseline justify-between gap-3 px-1">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
              {bucket.label} · {bucket.items} {bucket.items === 1 ? "item" : "items"}
            </span>
            <span className="tabular text-[13px] font-medium text-ink">
              {formatMoney(bucket.value)}
            </span>
          </div>

          {bucket.rows.length === 0 ? (
            <Empty>Nothing in this bucket.</Empty>
          ) : (
            <>
              <ul className="divide-y divide-line rounded-xl border border-line">
                {rows.map((row) => (
                  <li key={row.item} className="flex items-baseline gap-3 px-3 py-2.5">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] text-ink">{row.item}</span>
                      <span className="mt-0.5 block text-[11px] text-ink-3">
                        {formatQty(row.qty, row.unit)}
                        {row.lastSold
                          ? ` · last sold ${formatDate(row.lastSold)} (${row.daysSince}d)`
                          : " · never sold in this period"}
                      </span>
                    </span>
                    <span className="tabular shrink-0 text-[14px] text-ink">
                      {formatMoney(row.value)}
                    </span>
                  </li>
                ))}
              </ul>

              {bucket.rows.length > LIMIT && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="mt-2 px-1 text-xs font-medium text-accent"
                >
                  {showAll
                    ? `Show the top ${LIMIT}`
                    : `Show all ${bucket.rows.length} items →`}
                </button>
              )}

              <div className="mt-3">
                <ShareBar
                  csv={[
                    {
                      label: "Export this bucket",
                      filename: `stock-${bucket.label.replace(/[^\w]+/g, "-")}`,
                      build: () => [
                        ["Item", "Quantity", "Unit", "Value", "Last sold", "Days since"],
                        // The WHOLE bucket, not the page on screen: an export
                        // that silently stops at 40 rows is worse than none.
                        ...bucket.rows.map((r) => [
                          r.item,
                          r.qty,
                          r.unit,
                          r.value.toFixed(2),
                          r.lastSold ?? "never",
                          r.daysSince ?? "",
                        ]),
                      ],
                    },
                  ]}
                />
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Price spread, with each item's sales opened.
 *
 * The range is only half the finding. "Sold between ₹0 and ₹2,200" prompts
 * exactly one question — to whom, and when — and until that is one tap away the
 * chart reports a problem nobody can act on.
 */
function PriceSpreadChart({ snapshot, groups }: Pick<Ctx, "snapshot" | "groups">) {
  const [open, setOpen] = useState<string | null>(null);

  const rows = useMemo(() => priceSpread(snapshot, 3, groups), [snapshot, groups]);
  const item = rows.find((r) => r.item === open) ?? null;
  const free = rows.filter((r) => r.nearlyFree).length;

  return (
    <>
      <p className="mb-3 text-sm text-ink-2">
        <strong className="text-ink">{rows.length}</strong> items sold at more than one price.
        {free > 0 && ` ${free} went out for effectively nothing at least once.`}
      </p>

      <RangeDots
        rows={rows.map((r) => ({
          label: r.item,
          low: r.low,
          median: r.median,
          high: r.high,
          ...(r.nearlyFree ? { note: "Sold for effectively nothing at least once" } : {}),
        }))}
        selected={open}
        onSelect={setOpen}
      />

      {item === null ? (
        <p className="mt-3 px-1 text-xs text-ink-3">
          <strong className="text-ink-2">Tap an item to see every sale</strong> — the date, the
          rate and the customer.
        </p>
      ) : (
        <div className="mt-4">
          <div className="mb-2 flex items-baseline justify-between gap-3 px-1">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
              {item.item} · {item.sales} sales
            </span>
            <span className="tabular text-[13px] text-ink-3">
              {formatMoney(item.low)} – {formatMoney(item.high)}
            </span>
          </div>

          <ul className="divide-y divide-line rounded-xl border border-line">
            {item.lines.map((line) => (
              <li
                key={`${line.voucherId}-${line.date}-${line.rate}`}
                className="flex items-baseline gap-3 px-3 py-2.5"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] text-ink">
                    {line.customer || "(no customer on the voucher)"}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-ink-3">
                    {formatDate(line.date)} · {line.voucherNumber} · qty {line.qty}
                  </span>
                </span>
                <span
                  className={
                    "tabular shrink-0 text-[14px] " +
                    (line.rate === item.low && item.nearlyFree ? "font-semibold text-warn" : "text-ink")
                  }
                >
                  {formatMoney(line.rate)}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-3">
            <ShareBar
              csv={[
                {
                  label: "Export these sales",
                  filename: `price-${item.item.replace(/[^\w]+/g, "-")}`,
                  build: () => [
                    ["Date", "Voucher", "Customer", "Quantity", "Rate"],
                    ...item.lines.map((l) => [
                      l.date,
                      l.voucherNumber,
                      l.customer,
                      l.qty,
                      l.rate.toFixed(2),
                    ]),
                  ],
                },
              ]}
            />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * RFM, with the cells and segments opened.
 *
 * A heatmap you cannot reach into is a pretty picture. The value of this chart
 * is entirely "show me the thirteen who are Lost", so both the grid and the
 * segment bars open into the names.
 */
function RfmChart({ snapshot, groups, asOf }: Ctx) {
  const [cell, setCell] = useState<{ x: number; y: number } | null>(null);
  const [segment, setSegment] = useState<string | null>(null);

  const rfm = useMemo(() => deriveRfm(snapshot, asOf, groups), [snapshot, groups, asOf]);

  const shown = useMemo(() => {
    if (cell) {
      return {
        title: `Recency ${cell.y}/5 · frequency ${cell.x}/5`,
        rows: rfm.customers.filter((c) => c.r === cell.y && c.f === cell.x),
      };
    }
    if (segment) {
      return { title: segment, rows: rfm.customers.filter((c) => c.segment === segment) };
    }
    return null;
  }, [cell, segment, rfm]);

  return (
    <>
      <Heatmap
        grid={rfm.grid}
        xLabel="how often they buy →"
        yLabel="how recently →"
        selected={cell}
        onSelect={(next) => {
          setCell(next);
          // The two views answer the same question differently; showing both at
          // once would leave the reader unsure which list they are looking at.
          setSegment(null);
        }}
        describe={(f, r, count) =>
          count === 0
            ? `No customers at recency ${r}, frequency ${f}.`
            : `${count} customer${count === 1 ? "" : "s"} — recency ${r}/5, frequency ${f}/5. Tap to list them.`
        }
      />

      <div className="mt-5">
        <SectionTitle>Segments by value</SectionTitle>
        <ul className="space-y-1">
          {rfm.segments.map((s) => (
            <li key={s.segment}>
              <button
                type="button"
                onClick={() => {
                  setSegment(segment === s.segment ? null : s.segment);
                  setCell(null);
                }}
                aria-expanded={segment === s.segment}
                className={
                  "flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors active:bg-surface-2 " +
                  (segment === s.segment ? "bg-surface-2" : "")
                }
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] text-ink">{s.segment}</span>
                  <span className="text-[11px] text-ink-3">
                    {s.count} {s.count === 1 ? "customer" : "customers"}
                  </span>
                </span>
                <span className="tabular shrink-0 text-[14px] text-ink">{formatMoney(s.value)}</span>
                <svg
                  viewBox="0 0 24 24"
                  className="h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform"
                  style={{ transform: segment === s.segment ? "rotate(90deg)" : "none" }}
                  aria-hidden
                >
                  <path
                    d="M9 5l7 7-7 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {shown && (
        <div className="mt-4">
          <div className="mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
            {shown.title} · {shown.rows.length}{" "}
            {shown.rows.length === 1 ? "customer" : "customers"}
          </div>

          {shown.rows.length === 0 ? (
            <Empty>Nobody here.</Empty>
          ) : (
            <ul className="divide-y divide-line rounded-xl border border-line">
              {shown.rows.map((c) => (
                <li key={c.ledger}>
                  <button
                    type="button"
                    onClick={() => navigate({ id: "ledger", param: c.ledger })}
                    className="flex w-full items-baseline gap-3 px-3 py-2.5 text-left transition-colors active:bg-surface-2"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] text-ink">{c.ledger}</span>
                      <span className="mt-0.5 block text-[11px] text-ink-3">
                        last bought {c.recencyDays} days ago · {c.frequency}{" "}
                        {c.frequency === 1 ? "invoice" : "invoices"}
                      </span>
                    </span>
                    <span className="tabular shrink-0 text-[14px] text-ink">
                      {formatMoney(c.monetary)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="mt-3 text-xs text-ink-3">
        Scores are quintiles of your own customers, not fixed bands — a distributor and a corner
        shop have nothing in common, so any absolute threshold would be wrong for one of them.
      </p>
    </>
  );
}

export const CATEGORIES: Category[] = [
  { id: "sales", title: "Sales", blurb: "growth, timing and where it comes from", charts: SALES },
  { id: "products", title: "Products", blurb: "what sells, and at what price", charts: PRODUCTS },
  { id: "stock", title: "Stock", blurb: "what is moving and what is stuck", charts: STOCK },
  { id: "customers", title: "Customers", blurb: "who matters, and who is slipping", charts: CUSTOMERS },
  { id: "financial", title: "Financial", blurb: "the shape of the business", charts: FINANCIAL },
];

/* ─────────────────────────────────────────────────────────────── the screen ── */

export function Graphs() {
  const { snapshot, groups, asOf } = useApp();
  const [openCategory, setOpenCategory] = useState<string | null>("sales");
  const [openChart, setOpenChart] = useState<string | null>(null);

  const ctx = useMemo(
    () => (snapshot && groups ? { snapshot, groups, asOf } : null),
    [snapshot, groups, asOf],
  );

  if (!ctx) return <Empty>Nothing loaded.</Empty>;

  const selected = CATEGORIES.flatMap((c) => c.charts).find((c) => c.id === openChart);

  // One chart at a time, full width. Two charts side by side on a phone means
  // two charts nobody can read.
  if (selected) {
    const why = selected.unavailable?.(ctx) ?? null;
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setOpenChart(null)}
          className="flex items-center gap-1.5 text-sm font-medium text-accent"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
            <path
              d="M15 5l-7 7 7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          All graphs
        </button>

        <Card title={selected.title}>
          <p className="mb-4 text-sm text-ink-3">{selected.question}</p>
          {why ? <Empty>{why}</Empty> : selected.render(ctx)}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {CATEGORIES.map((category) => {
        const open = openCategory === category.id;
        return (
          <Card key={category.id} flush>
            <button
              type="button"
              onClick={() => setOpenCategory(open ? null : category.id)}
              className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
              aria-expanded={open}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-medium text-ink">{category.title}</span>
                <span className="mt-0.5 block text-xs text-ink-3">{category.blurb}</span>
              </span>
              <span className="shrink-0 text-xs text-ink-3">{category.charts.length}</span>
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4 shrink-0 text-ink-3 transition-transform"
                style={{ transform: open ? "rotate(90deg)" : "none" }}
                aria-hidden
              >
                <path
                  d="M9 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {open && (
              <ul className="divide-y divide-line border-t border-line">
                {category.charts.map((chart) => {
                  const why = chart.unavailable?.(ctx) ?? null;
                  return (
                    <li key={chart.id}>
                      <button
                        type="button"
                        disabled={why !== null}
                        onClick={() => setOpenChart(chart.id)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-surface-2 disabled:opacity-55"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[15px] text-ink">{chart.title}</span>
                          <span className="mt-0.5 block text-xs text-ink-3">
                            {why ?? chart.question}
                          </span>
                        </span>
                        {why === null && (
                          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-ink-3" aria-hidden>
                            <path
                              d="M9 5l7 7-7 7"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        );
      })}
    </div>
  );
}
